import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { purchaseApi } from '../api/purchase';

interface Invoice { id: number; invoice_no: string; supplier_name?: string; invoice_amount?: number; invoice_date?: string; }

interface Props {
  value: string;
  onSelect: (inv: Invoice | null) => void;
  contactId?: string;
  placeholder?: string;
  fontSize?: number;
}

const InvoicePicker: React.FC<Props> = ({ value, onSelect, contactId, placeholder = 'General payment — type to search invoice', fontSize = 12 }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Invoice[]>([]);
  const [open, setOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [pos, setPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });
  const [highlight, setHighlight] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => { if (!value) setSelectedLabel(''); }, [value]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      const params: any = { search: query, limit: 50 };
      if (contactId) params.contact_id = contactId;
      purchaseApi.supplierInvoices(params)
        .then(r => { setResults(r.data.data || []); setHighlight(-1); })
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [query, open, contactId]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (inputRef.current && !inputRef.current.contains(t) && dropdownRef.current && !dropdownRef.current.contains(t)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (highlight >= 0 && itemRefs.current[highlight]) itemRefs.current[highlight]?.scrollIntoView({ block: 'nearest' });
  }, [highlight]);

  const openDropdown = () => {
    if (inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + window.scrollY, left: r.left + window.scrollX, width: r.width });
    }
    setOpen(true);
    setQuery('');
  };

  const handleSelect = (inv: Invoice | null) => {
    setSelectedLabel(inv ? inv.invoice_no + ' — ' + Number(inv.invoice_amount || 0).toFixed(2) : '');
    setQuery('');
    setOpen(false);
    setHighlight(-1);
    onSelect(inv);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open) { if (e.key === 'ArrowDown' || e.key === 'Enter') openDropdown(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (highlight >= 0 && results[highlight]) handleSelect(results[highlight]); }
    else if (e.key === 'Escape') { setOpen(false); setHighlight(-1); }
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        className="form-control"
        style={{ fontSize }}
        placeholder={placeholder}
        value={open ? query : selectedLabel}
        onFocus={openDropdown}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
      />
      {open && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'absolute', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999,
            background: '#ffffff', border: '1px solid var(--bdr)', borderRadius: 6,
            maxHeight: 260, overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,0.25)', marginTop: 2
          }}
        >
          <div
            onClick={() => handleSelect(null)}
            style={{ padding: '8px 10px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid var(--bdr)', fontStyle: 'italic', color: 'var(--tx3)' }}
          >
            General payment (no invoice)
          </div>
          {results.length === 0 ? (
            <div style={{ padding: 10, fontSize: 12, color: 'var(--tx3)' }}>{query.trim() ? 'No matches' : 'Type to search...'}</div>
          ) : results.map((inv, idx) => (
            <div
              key={inv.id}
              ref={el => { itemRefs.current[idx] = el; }}
              onClick={() => handleSelect(inv)}
              onMouseEnter={() => setHighlight(idx)}
              style={{
                padding: '8px 10px', fontSize: 12, cursor: 'pointer', borderBottom: '1px solid var(--bdr)',
                background: idx === highlight ? '#e8f0fe' : '#ffffff'
              }}
            >
              <div style={{ fontWeight: 600 }}>{inv.invoice_no} — {Number(inv.invoice_amount || 0).toFixed(2)}</div>
              <div style={{ color: 'var(--tx3)', fontSize: 11 }}>
                {inv.supplier_name || ''}{inv.invoice_date ? '  |  ' + new Date(inv.invoice_date).toLocaleDateString() : ''}
              </div>
            </div>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default InvoicePicker;
